// 地図画面(閲覧モード)
// 現在地表示・精度円・レイヤー切替・ズームコントロール
//
// 記録モードはこの画面の拡張として tracking.js で実装

import { createMap, createPositionMarker, setupAccuracyCircle, updateAccuracyCircle, switchLayer, setupFootprintsLayer, updateFootprints, isFootprintsEnabled, setupOsmHikingLayer, updateOsmHiking, TILE_LAYERS } from '../map.js';
import { showScreen, toast, escapeHtml, isValidLatLng, fmtElevation, applyViewportHeight, adjustMapControls } from '../ui.js';
import { getAllTracks } from '../cache.js';
import { fetchHikingPaths, isOsmHikingEnabled } from '../osm-hiking.js';

let activeMap = null;
let activeWatchId = null;
let activeMarker = null;
let followMode = true;
let currentLayer = 'std';
let osmFetchTimer = null;
let osmAbortController = null;

function debouncedFetchOsm() {
  if (osmFetchTimer) clearTimeout(osmFetchTimer);
  osmFetchTimer = setTimeout(async () => {
    if (!activeMap) return;
    const zoom = activeMap.getZoom();
    if (zoom < 13) {
      // 広域で取得すると重いので z13 以上のみ
      updateOsmHiking(activeMap, { type: 'FeatureCollection', features: [] });
      return;
    }
    const b = activeMap.getBounds();
    const bbox = {
      south: b.getSouth(),
      west: b.getWest(),
      north: b.getNorth(),
      east: b.getEast(),
    };
    if (osmAbortController) osmAbortController.abort();
    osmAbortController = new AbortController();
    const data = await fetchHikingPaths(bbox, osmAbortController.signal);
    if (activeMap) updateOsmHiking(activeMap, data);
  }, 1500);
}

// 起動オプション:
//   mode: 'view' | 'record' (P1段階では view のみ実装)
export function showMapView(opts = {}) {
  cleanupMapView();

  const html = `
    <div class="map-stage" id="map-stage">
      <div id="map-container" class="map-container" aria-label="登山地図"></div>

      <div class="map-top">
        <button class="btn btn--icon" data-action="back" type="button" aria-label="戻る" style="background:var(--bg);border:1px solid var(--line);pointer-events:auto;">
          <svg class="icon"><use href="#i-back"/></svg>
        </button>
        <span class="map-top__pill" id="map-status">位置情報を取得中…</span>
      </div>

      <div class="map-controls">
        <button class="btn btn--icon" data-action="zoom-in" type="button" aria-label="拡大">
          <svg class="icon"><use href="#i-zoom-in"/></svg>
        </button>
        <button class="btn btn--icon" data-action="zoom-out" type="button" aria-label="縮小">
          <svg class="icon"><use href="#i-zoom-out"/></svg>
        </button>
        <button class="btn btn--icon ${followMode ? '' : ''}" data-action="follow" id="btn-follow" type="button" aria-label="現在地に追従">
          <svg class="icon"><use href="#i-target"/></svg>
        </button>
        <button class="btn btn--icon" data-action="layer" type="button" aria-label="地図レイヤー切替">
          <svg class="icon"><use href="#i-layers"/></svg>
        </button>
      </div>
    </div>
  `;

  const screen = showScreen('map-view', html, { fullbleed: true });

  // PWA standalone の高さ問題対策
  const stage = screen.querySelector('#map-stage');
  applyViewportHeight(stage);
  setTimeout(() => adjustMapControls(stage), 100);
  window.addEventListener('resize', () => {
    applyViewportHeight(stage);
    adjustMapControls(stage);
  });
  window.addEventListener('orientationchange', () => setTimeout(() => {
    applyViewportHeight(stage);
    adjustMapControls(stage);
  }, 200));

  // 初期中心: localStorage の lastPosition、なければ富士山
  let initialCenter = [138.7274, 35.3606];
  let initialZoom = 12;
  try {
    const saved = JSON.parse(localStorage.getItem('sentier.lastPosition') || 'null');
    if (saved && isValidLatLng(saved.lat, saved.lng)) {
      initialCenter = [saved.lng, saved.lat];
      initialZoom = 14;
    }
  } catch (e) {
    /* noop */
  }

  // screen の display:block 反映を待ってから MapLibre 初期化
  // (初期化時にコンテナサイズが 0 だと真っ暗な地図になる)
  requestAnimationFrame(() => {
    activeMap = createMap('map-container', {
      center: initialCenter,
      zoom: initialZoom,
      layer: currentLayer,
    });

    activeMap.on('load', async () => {
      activeMap.resize();
      setupAccuracyCircle(activeMap);
      // 足跡レイヤー
      if (isFootprintsEnabled()) {
        setupFootprintsLayer(activeMap);
        const tracks = await getAllTracks();
        updateFootprints(activeMap, tracks);
      }
      // OSM 登山道オーバーレイ
      if (isOsmHikingEnabled()) {
        setupOsmHikingLayer(activeMap);
        debouncedFetchOsm();
      }
      startWatching();
    });

    activeMap.on('dragstart', () => {
      setFollow(false);
    });

    activeMap.on('moveend', () => {
      if (isOsmHikingEnabled()) debouncedFetchOsm();
    });

    // ロングタップ(contextmenu)で ActionSheet
    activeMap.on('contextmenu', async (e) => {
      const { showActionSheet, copyToClipboard, showInputDialog } = await import('../ui.js');
      const choice = await showActionSheet({
        title: '地図上のこの地点',
        body: `${e.lngLat.lat.toFixed(5)}, ${e.lngLat.lng.toFixed(5)}`,
        options: [
          { label: 'ここの天気を見る', value: 'weather', kind: 'ink' },
          { label: '地点メモを追加', value: 'poi' },
          { label: '座標をコピー', value: 'copy', kind: 'ghost' },
        ],
      });
      if (!choice) return;
      if (choice === 'copy') {
        await copyToClipboard(`${e.lngLat.lat.toFixed(5)}, ${e.lngLat.lng.toFixed(5)}`, '座標をコピーしました');
      } else if (choice === 'poi') {
        const title = await showInputDialog({ title: '地点メモ', placeholder: '例: 水場 / 分岐' });
        if (!title) return;
        const { savePoi } = await import('../cache.js');
        await savePoi({ lat: e.lngLat.lat, lng: e.lngLat.lng, title: title.trim(), kind: 'note' });
        toast('地点を保存しました');
      } else if (choice === 'weather') {
        try {
          localStorage.setItem('sentier.lastPosition', JSON.stringify({ lat: e.lngLat.lat, lng: e.lngLat.lng, ts: Date.now() }));
        } catch (_) { /* noop */ }
        const { showWeatherScreen } = await import('./weather.js');
        cleanupMapView();
        showWeatherScreen();
      }
    });

    // 念のため、ロード前にも一度 resize() を試みる
    setTimeout(() => { if (activeMap) activeMap.resize(); }, 200);
  });

  bindControls(screen);
}

function bindControls(screen) {
  screen.querySelector('[data-action="zoom-in"]')?.addEventListener('click', () => {
    if (!activeMap) return;
    activeMap.zoomTo(activeMap.getZoom() + 1, { duration: 200 });
  });
  screen.querySelector('[data-action="zoom-out"]')?.addEventListener('click', () => {
    if (!activeMap) return;
    activeMap.zoomTo(activeMap.getZoom() - 1, { duration: 200 });
  });
  screen.querySelector('[data-action="follow"]')?.addEventListener('click', () => {
    setFollow(true);
    centerOnLastPosition();
  });
  screen.querySelector('[data-action="layer"]')?.addEventListener('click', () => {
    cycleLayer();
  });
}

function cycleLayer() {
  const order = ['std', 'pale', 'relief', 'photo', 'vector'];
  const idx = order.indexOf(currentLayer);
  currentLayer = order[(idx + 1) % order.length];
  switchLayer(activeMap, currentLayer);
  // 切替時にレイヤーが再構築されるので、現在地レイヤー類を再セットアップ
  activeMap.once('idle', async () => {
    setupAccuracyCircle(activeMap);
    if (isFootprintsEnabled()) {
      setupFootprintsLayer(activeMap);
      const tracks = await getAllTracks();
      updateFootprints(activeMap, tracks);
    }
    if (isOsmHikingEnabled()) {
      setupOsmHikingLayer(activeMap);
      debouncedFetchOsm();
    }
    centerOnLastPosition();
  });
  toast(`地図: ${TILE_LAYERS[currentLayer].name}`);
}

function setFollow(on) {
  followMode = on;
  const btn = document.getElementById('btn-follow');
  if (btn) {
    btn.style.background = on ? 'var(--ink)' : 'var(--bg)';
    btn.style.color = on ? 'var(--bg)' : 'var(--ink)';
  }
}

function centerOnLastPosition() {
  try {
    const saved = JSON.parse(localStorage.getItem('sentier.lastPosition') || 'null');
    if (saved && isValidLatLng(saved.lat, saved.lng) && activeMap) {
      activeMap.easeTo({ center: [saved.lng, saved.lat], duration: 400 });
    }
  } catch (e) {
    /* noop */
  }
}

// ============ Geolocation 監視 ============

function startWatching() {
  if (!('geolocation' in navigator)) {
    setStatus('この端末では位置情報を取得できません');
    return;
  }

  setStatus('位置情報を取得中…');

  activeWatchId = navigator.geolocation.watchPosition(
    (pos) => onPositionUpdate(pos),
    (err) => onPositionError(err),
    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000,
    }
  );
}

function onPositionUpdate(pos) {
  if (!activeMap) return;
  const { latitude, longitude, accuracy, altitude } = pos.coords;
  if (!isValidLatLng(latitude, longitude)) return;

  // localStorage に保存(次回起動時の初期中心用)
  try {
    localStorage.setItem(
      'sentier.lastPosition',
      JSON.stringify({ lat: latitude, lng: longitude, ts: Date.now() })
    );
  } catch (e) {
    /* noop: ストレージ満杯時 */
  }

  // マーカー更新 or 新規作成
  if (activeMarker) {
    activeMarker.setLngLat([longitude, latitude]);
  } else {
    activeMarker = createPositionMarker(activeMap, longitude, latitude);
  }

  // 精度円更新
  if (typeof accuracy === 'number' && accuracy > 0) {
    updateAccuracyCircle(activeMap, longitude, latitude, accuracy);
  }

  // 追従モードなら中心を合わせる
  if (followMode) {
    activeMap.easeTo({ center: [longitude, latitude], duration: 350 });
  }

  // ステータス更新
  const altText = typeof altitude === 'number' ? ` · ${fmtElevation(altitude)}` : '';
  setStatus(`精度 ±${Math.round(accuracy)}m${altText}`);
}

function onPositionError(err) {
  let msg = '位置情報を取得できません';
  if (err.code === 1) msg = '位置情報の利用が拒否されています';
  else if (err.code === 2) msg = '位置情報が取得できません(電波弱)';
  else if (err.code === 3) msg = '位置情報の取得がタイムアウトしました';
  setStatus(msg);
  console.warn('Geolocation error', err);
}

function setStatus(text) {
  const el = document.getElementById('map-status');
  if (el) el.textContent = text;
}

// ============ クリーンアップ ============

export function cleanupMapView() {
  if (activeWatchId !== null) {
    navigator.geolocation.clearWatch(activeWatchId);
    activeWatchId = null;
  }
  if (activeMarker) {
    activeMarker.remove();
    activeMarker = null;
  }
  if (activeMap) {
    activeMap.remove();
    activeMap = null;
  }
}
